import { useEffect } from "react";

const SITE = "IPVALUE MARKETPLACE";
const BASE = "https://ipvaluemarketplace.com";
const DEFAULT_TITLE = "IPVALUE Marketplace — Closeout & Overstock Furniture, Appliances & Pallets | Grand Rapids, MI";
const DEFAULT_IMAGE = `${BASE}/og-image.png`;

function setMeta(selector, attr, value) {
  let tag = document.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    const [key, name] = selector.includes("property=")
      ? ["property", selector.split('property="')[1].split('"')[0]]
      : ["name", selector.split('name="')[1].split('"')[0]];
    tag.setAttribute(key, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute(attr, value);
}

export function usePageMeta(title, description, opts = {}) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE}` : DEFAULT_TITLE;
    document.title = fullTitle;
    const desc = description || "Brand-new closeout and overstock furniture, appliances, e-bikes and liquidation pallets in Grand Rapids, Michigan. In-store pickup or local delivery.";
    // §1 self-canonical — the page's own absolute URL, no query strings
    const path = (opts.canonicalPath || window.location.pathname).replace(/\/+$/, "") || "/";
    const url = path === "/" ? `${BASE}/` : `${BASE}${path}`;
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement("link");
      canon.setAttribute("rel", "canonical");
      document.head.appendChild(canon);
    }
    canon.setAttribute("href", url);
    // §5 per-page social tags
    setMeta('meta[name="description"]', "content", desc);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[property="og:title"]', "content", fullTitle);
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[name="twitter:title"]', "content", fullTitle);
    setMeta('meta[name="twitter:description"]', "content", desc);
    setMeta('meta[property="og:image"]', "content", opts.image || DEFAULT_IMAGE);
    setMeta('meta[name="robots"]', "content", opts.noindex ? "noindex, nofollow" : "index, follow");
  }, [title, description, opts.canonicalPath, opts.image, opts.noindex]);
}

function useJsonLd(id, data) {
  useEffect(() => {
    if (!data) return;
    document.getElementById(id)?.remove();
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    el.text = JSON.stringify(data);
    document.head.appendChild(el);
    return () => document.getElementById(id)?.remove();
  }, [id, JSON.stringify(data)]); // eslint-disable-line react-hooks/exhaustive-deps
}

// §4 Product rich-result schema on product pages (+ AggregateRating when on-site reviews exist)
export function useProductSchema(product, reviewStats) {
  useJsonLd(
    "product-schema",
    product && {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      description: product.description || product.name,
      sku: product.sku || product.id,
      image: (product.images || [])[0]
        ? (product.images[0].startsWith("http") ? product.images[0] : `${BASE}/api/files/${product.images[0]}`)
        : undefined,
      brand: { "@type": "Brand", name: product.brand || SITE },
      ...(reviewStats?.count > 0 && {
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: reviewStats.average,
          reviewCount: reviewStats.count,
          bestRating: 5,
        },
        review: (reviewStats.items || []).slice(0, 5).map((r) => ({
          "@type": "Review",
          author: { "@type": "Person", name: r.name },
          reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 },
          reviewBody: r.text,
          datePublished: (r.created_at || "").slice(0, 10) || undefined,
        })),
      }),
      offers: {
        "@type": "Offer",
        url: `${BASE}/product/${product.slug}`,
        priceCurrency: "USD",
        price: product.price,
        availability:
          product.stock > 0 || product.fulfillment_type === "order_in"
            ? "https://schema.org/InStock"
            : "https://schema.org/OutOfStock",
        seller: { "@type": "Organization", name: SITE },
      },
    }
  );
}

// §4 ItemList schema on category pages
export function useItemListSchema(categoryName, products) {
  useJsonLd(
    "itemlist-schema",
    products?.length
      ? {
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: `${categoryName || "Shop All"} — ${SITE}`,
          itemListElement: products.slice(0, 30).map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: p.name,
            url: `${BASE}/product/${p.slug}`,
          })),
        }
      : null
  );
}

// FAQPage schema — feeds Google FAQ rich results + AI answer engines
export function useFAQSchema(faqs) {
  useJsonLd(
    "faq-schema",
    faqs?.length
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }
      : null
  );
}

// §4 Service schema on /build-pallet
export function useBuildPalletServiceSchema() {
  useJsonLd("service-schema", {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Build Your Own Pallet",
    serviceType: "Custom liquidation pallet building",
    description:
      "Pick your items from live in-store availability — one pallet price with merchandise included (3 items $500, 5 items $900, 7 items $1,500, 11 items $2,200) plus a flat $299 customization fee. Ready for pickup or local delivery in 2–3 business days.",
    provider: { "@type": "Store", name: SITE, telephone: "+1-616-410-0158" },
    areaServed: [
      { "@type": "City", name: "Grand Rapids" },
      { "@type": "City", name: "Kentwood, MI" },
      { "@type": "City", name: "Grandville, MI" },
      { "@type": "City", name: "Byron Center, MI" },
      { "@type": "AdministrativeArea", name: "West Michigan" },
    ],
    url: `${BASE}/build-pallet`,
  });
}
